import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  AvailabilityRangeInput,
  CreateVisitBlackoutInput,
  VisitAvailabilityRuleDto,
  VisitBlackoutDto,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

export function useAvailabilityRules() {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["visit-availability", clientId],
    queryFn: () =>
      api.get<{ rules: VisitAvailabilityRuleDto[] }>("/crm/visits/availability"),
    enabled: !!clientId,
  });
}

export function useSaveAdvisorSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ranges }: { userId: string; ranges: AvailabilityRangeInput[] }) =>
      api.put<{ rules: VisitAvailabilityRuleDto[] }>(
        `/crm/visits/availability/${userId}`,
        { ranges },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["visit-availability"] });
      void queryClient.invalidateQueries({ queryKey: ["visit-slots"] });
      toast.success("Disponibilidad guardada.");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useBlackouts() {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["visit-blackouts", clientId],
    queryFn: () => api.get<{ blackouts: VisitBlackoutDto[] }>("/crm/visits/blackouts"),
    enabled: !!clientId,
  });
}

export function useCreateBlackout() {
  const queryClient = useQueryClient();
  const clientId = useScopedClientId();
  return useMutation({
    mutationFn: (input: CreateVisitBlackoutInput) =>
      api.post<{ blackout: VisitBlackoutDto }>("/crm/visits/blackouts", input),
    onSuccess: (data) => {
      // Write the created row into the list cache directly so it appears the
      // moment the dialog closes; the invalidation below re-syncs with the
      // server ordering afterwards.
      queryClient.setQueryData<{ blackouts: VisitBlackoutDto[] }>(
        ["visit-blackouts", clientId],
        (old) => ({
          blackouts: [...(old?.blackouts ?? []), data.blackout].sort((a, b) =>
            a.dateFrom.localeCompare(b.dateFrom),
          ),
        }),
      );
      void queryClient.invalidateQueries({ queryKey: ["visit-blackouts"] });
      void queryClient.invalidateQueries({ queryKey: ["visit-slots"] });
      toast.success("Excepción agregada.");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useDeleteBlackout() {
  const queryClient = useQueryClient();
  const clientId = useScopedClientId();
  return useMutation({
    mutationFn: (id: string) => api.delete<null>(`/crm/visits/blackouts/${id}`),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<{ blackouts: VisitBlackoutDto[] }>(
        ["visit-blackouts", clientId],
        (old) => ({ blackouts: (old?.blackouts ?? []).filter((b) => b.id !== id) }),
      );
      void queryClient.invalidateQueries({ queryKey: ["visit-blackouts"] });
      void queryClient.invalidateQueries({ queryKey: ["visit-slots"] });
      toast.success("Excepción eliminada.");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
